import { Warehouse } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useQuery } from '@tanstack/react-query';
import { api, getApiErrorMessage, getApiErrorStatus } from '@/shared/api/axios';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Badge } from '@/shared/components/ui/Badge';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { formatINR } from '@/shared/utils/currency';

export function AssetsPage() {
  const { data: assets, isLoading, error } = useQuery({ queryKey: ['accounts', 'fixed-assets'], queryFn: async () => { const { data } = await api.get('/api/accounts/fixed-assets'); return data as any[]; } });

  if (isLoading) return <div aria-busy="true" className="space-y-3 p-6"><Skeleton className="h-12 w-full rounded-card" /><Skeleton className="h-[400px] w-full rounded-card" /></div>;

  if (error) {
    const denied = getApiErrorStatus(error) === 403;
    return (
      <div role="alert" className={`m-6 rounded-card border p-4 text-sm ${denied ? 'border-warning/25 bg-warning/10 text-warning-text' : 'border-danger/20 bg-danger/10 text-danger-text'}`}>
        <p className="font-semibold">{denied ? 'Permission denied' : 'Could not load the asset register'}</p>
        <p className="mt-1 text-body-sm">{denied
          ? 'Your role cannot read fixed assets. Ask an Accounts administrator for read access.'
          : getApiErrorMessage(error, 'The asset register did not respond. Retry from the Accounts page.')}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <PageHeader icon={Warehouse} eyebrow="Finance" title="Fixed Assets" description={<>Asset register, depreciation and current book value</>} />
      <Card className="shadow-xs">
        <CardBody className="p-0">
          <div className="overflow-auto max-h-[500px]">
            <table className="w-full text-body-sm text-left">
              <thead className="text-label-caps uppercase text-on-surface-variant bg-muted sticky top-0"><tr><th className="px-4 py-2.5">Code</th><th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Category</th><th className="px-4 py-2.5 text-right">Cost</th><th className="px-4 py-2.5 text-right">Book Value</th><th className="px-4 py-2.5">Method</th><th className="px-4 py-2.5">Status</th></tr></thead>
              <tbody className="divide-y divide-border">
                {(!assets || assets.length === 0) && <tr><td colSpan={7}><EmptyState title="No fixed assets registered" description="Add an asset to start tracking its cost and depreciation." /></td></tr>}
                {assets?.map((a: any) => (
                  <tr key={a._id} className="hover:bg-muted/50">
                    <td className="px-4 py-2.5 font-mono text-xs">{a.assetCode || '—'}</td>
                    <td className="px-4 py-2.5 font-medium">{a.name}</td>
                    <td className="px-4 py-2.5 capitalize">{a.category || '—'}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums">{a.purchaseCost == null ? '—' : formatINR(a.purchaseCost)}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums font-medium">{a.currentBookValue == null ? '—' : formatINR(a.currentBookValue)}</td>
                    <td className="px-4 py-2.5 text-label-caps uppercase text-on-surface-variant">{a.depreciationMethod === 'straight_line' ? 'SLM' : 'WDV'}</td>
                    <td className="px-4 py-2.5"><Badge tone={a.status === 'active' ? 'success' : a.status === 'disposed' ? 'danger' : 'neutral'}>{a.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
